import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, categoryById } from "@/lib/categories";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const CategorySelect = ({ value, onChange, testId, className = "" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`bg-[#111726] border-slate-800 text-slate-100 ${className}`}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-[#111726] border-slate-800 text-slate-100">
      {CATEGORIES.map((c) => (
        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const PreviewTable = ({ rows, setRows }) => {
  const update = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="max-h-[40vh] overflow-y-auto rounded-lg border border-slate-800" data-testid="import-preview-table">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[24px_1fr_170px] items-center gap-3 px-3 py-2 border-b border-slate-800 last:border-0">
          <Checkbox data-testid={`import-row-include-${i}`} checked={r.include} onCheckedChange={(v) => update(i, { include: !!v })} />
          <div className="min-w-0">
            <div className="text-sm text-slate-100 truncate">{r.title}</div>
            <div className="text-[11px] font-mono text-slate-500 truncate">
              {Object.keys(r.fields).filter((k) => k !== "notes").map((k) => categoryById(r.category).fields.find((f) => f.key === k)?.label || k).join(" · ")}
            </div>
          </div>
          <CategorySelect value={r.category} testId={`import-row-category-${i}`} className="h-8 text-xs"
            onChange={(v) => update(i, { category: v, fields: remap(r.fields, r.category, v) })} />
        </div>
      ))}
    </div>
  );
};

const ROLE_KEYS = {
  email: ["email", "password", "imap_host"], server: ["username", "password", "host_ip"],
  vpn: ["username", "password", "gateway_ip"], firewall: ["admin_user", "admin_password", "management_ip"],
  idrac: ["admin_user", "admin_password", "idrac_ip"], domain: ["registrar_user", "registrar_password", "domain_name"],
  license: ["support_email", "license_key", "vendor"], cloud: ["username", "password", "console_url"],
  other: ["username", "password", "url"],
};

function remap(fields, from, to) {
  const out = { ...fields };
  ROLE_KEYS[from].forEach((k, idx) => {
    if (k in out) {
      const v = out[k];
      delete out[k];
      out[ROLE_KEYS[to][idx]] = v;
    }
  });
  return out;
}

export default function ImportModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [kdbxPass, setKdbxPass] = useState("");
  const [defaultCat, setDefaultCat] = useState("other");
  const [rows, setRows] = useState(null);
  const [adminOnly, setAdminOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const isKdbx = file?.name?.toLowerCase().endsWith(".kdbx");

  const close = (v) => {
    if (!v) { setFile(null); setRows(null); setKdbxPass(""); setAdminOnly(false); }
    onOpenChange(v);
  };

  const preview = async () => {
    if (!file) return toast.error("Önce bir dosya seçin");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("default_category", defaultCat);
      if (isKdbx) fd.append("kdbx_password", kdbxPass);
      const { data } = await api.post("/import/preview", fd);
      if (!data.count) toast.error("Dosyada aktarılacak kayıt bulunamadı");
      setRows(data.rows.map((r) => ({ ...r, include: true })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dosya okunamadı");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    const selected = rows.filter((r) => r.include).map(({ include, ...r }) => r);
    if (!selected.length) return toast.error("En az bir kayıt seçin");
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { rows: selected, admin_only: adminOnly });
      toast.success(`${data.created} kayıt içe aktarıldı`);
      window.dispatchEvent(new CustomEvent("vaultops:refresh"));
      close(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktarım başarısız");
    } finally { setBusy(false); }
  };

  const included = rows?.filter((r) => r.include).length || 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="import-modal" className="bg-[#0d121f] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-emerald-400" /> Toplu İçe Aktarım
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Excel (.xlsx), CSV, KeePass (.kdbx) veya KeePass XML dosyası yükleyin. Sütunlar (başlık, kullanıcı, parola, url, notlar, kategori, grup) otomatik eşleştirilir.
          </DialogDescription>
        </DialogHeader>

        {!rows ? (
          <div className="grid gap-4 py-2">
            <label data-testid="import-dropzone" className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed border-slate-700 hover:border-emerald-500/50 bg-[#111726] cursor-pointer transition-colors">
              <Upload className="w-6 h-6 text-emerald-400" />
              <span className="text-sm text-slate-300">{file ? file.name : "Dosya seçmek için tıklayın"}</span>
              <span className="text-[11px] font-mono text-slate-500">.xlsx · .csv · .kdbx · .xml — en fazla 10 MB</span>
              <input data-testid="import-file-input" type="file" accept=".xlsx,.xlsm,.csv,.txt,.kdbx,.xml" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {isKdbx && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">KeePass Ana Parolası</Label>
                <Input data-testid="import-kdbx-password" type="password" value={kdbxPass} onChange={(e) => setKdbxPass(e.target.value)}
                  className="bg-[#111726] border-slate-800 text-slate-100 mt-1" />
                <p className="text-[11px] text-slate-500 mt-1">Parola sadece dosyayı açmak için kullanılır, saklanmaz.</p>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">Tanınmayan kayıtlar için kategori</Label>
              <CategorySelect value={defaultCat} onChange={setDefaultCat} testId="import-default-category" className="mt-1" />
            </div>
          </div>
        ) : (
          <div className="grid gap-3 py-2">
            <div className="text-sm text-slate-300">{rows.length} kayıt bulundu · <span className="text-emerald-400">{included} seçili</span>. Gerekirse kategorileri düzeltin.</div>
            <PreviewTable rows={rows} setRows={setRows} />
            {user?.role === "admin" && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#111726] border border-slate-800">
                <div className="text-sm text-slate-100">Aktarılan kayıtları sadece adminler görsün</div>
                <Switch data-testid="import-admin-only" checked={adminOnly} onCheckedChange={setAdminOnly} />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {rows && <Button variant="ghost" onClick={() => setRows(null)} className="text-slate-300 hover:bg-slate-800" data-testid="import-back-button">Geri</Button>}
          {!rows ? (
            <Button data-testid="import-preview-button" onClick={preview} disabled={busy || !file} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              {busy ? "Okunuyor..." : "Önizle"}
            </Button>
          ) : (
            <Button data-testid="import-commit-button" onClick={commit} disabled={busy || !included} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              {busy ? "Aktarılıyor..." : `${included} kaydı içe aktar`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
